import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { get, post } from "../../utils/makeRequest";

// Update token retrieval to be dynamic
const getToken = () => localStorage.getItem("token");

// Create subscription
export const createSubscription = createAsyncThunk(
  "payment/createSubscription",
  async (subscriptionData, { rejectWithValue }) => {
    const { plan, interval, customApiRequests } = subscriptionData;
    try {
      const payload = { plan, interval };

      console.log("🔍 DEBUG - Redux createSubscription params:", {
        plan,
        interval,
        customApiRequests,
      });

      // Add customApiRequests for custom plans
      if (customApiRequests) {
        payload.customApiRequests = customApiRequests;
      }

      console.log("🔍 DEBUG - Final payload being sent to API:", payload);

      const response = await post("/payment/create-subscription", payload, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Verify payment
export const verifyPayment = createAsyncThunk(
  "payment/verifyPayment",
  async (paymentData) => {
    const response = await post("/payment/verify-payment", paymentData, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    return response;
  }
);

// Cancel subscription
export const cancelSubscription = createAsyncThunk(
  "payment/cancelSubscription",
  async (subscription_id) => {
    const response = await post("/payment/cancel-subscription", 
      { subscription_id },
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return response;
  }
);

// Get subscription details
export const getSubscriptionDetails = createAsyncThunk(
  "payment/getSubscriptionDetails",
  async () => {
    const response = await get("/payment/subscription-details", {}, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response;
  }
);

// Get current user plan details
export const getCurrentUserPlan = createAsyncThunk(
  "payment/getCurrentUserPlan",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get("/payment/current-plan", {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Add new action for custom plan payment verification
export const verifyCustomPlanPayment = createAsyncThunk(
  "payment/verifyCustomPlanPayment",
  async (paymentData) => {
    const response = await post("/payment/verify-custom-plan-payment",
      paymentData,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return response;
  }
);

// Add new action for activating free plan after line 97
export const activateFreePlan = createAsyncThunk(
  "payment/activateFreePlan",
  async (_, { rejectWithValue }) => {
    try {
      const response = await post("/payment/activate-free-plan", {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

// Schedule plan cancellation (same base URL & auth as other payment APIs)
export const cancelPlan = createAsyncThunk(
  "payment/cancelPlan",
  async (reason, { rejectWithValue }) => {
    try {
      const response = await post(
        "/payment/cancel-plan",
        { reason: reason || "User requested cancellation" },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      return response;
    } catch (error) {
      const data = error.response?.data;
      const message =
        typeof data?.error === "string"
          ? data.error
          : data?.message || error.message || "Failed to cancel plan";
      return rejectWithValue({ error: message, details: data });
    }
  }
);

const paymentSlice = createSlice({
  name: "payment",
  initialState: {
    subscription: null,
    currentPlan: null,
    loading: false,
    error: null,
    planLoading: false,
    planError: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.planError = null;
    },
    clearPlanData: (state) => {
      state.currentPlan = null;
      state.planError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create subscription
      .addCase(createSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(createSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to create subscription";
      })

      // Verify payment
      .addCase(verifyPayment.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyPayment.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Cancel subscription
      .addCase(cancelSubscription.pending, (state) => {
        state.loading = true;
      })
      .addCase(cancelSubscription.fulfilled, (state) => {
        state.loading = false;
        state.subscription = null;
      })
      .addCase(cancelSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get subscription details
      .addCase(getSubscriptionDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSubscriptionDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.subscription = action.payload.subscription;
      })
      .addCase(getSubscriptionDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      // Get current user plan
      .addCase(getCurrentUserPlan.pending, (state) => {
        state.planLoading = true;
        state.planError = null;
      })
      .addCase(getCurrentUserPlan.fulfilled, (state, action) => {
        state.planLoading = false;
        state.currentPlan = action.payload.user_plan;
        state.planError = null;
      })
      .addCase(getCurrentUserPlan.rejected, (state, action) => {
        state.planLoading = false;
        state.planError =
          action.payload?.error || "Failed to fetch plan details";
      })
      // Activate free plan
      .addCase(activateFreePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateFreePlan.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(activateFreePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || "Failed to activate free plan";
      });
  },
});

export const { clearError, clearPlanData } = paymentSlice.actions;
export default paymentSlice.reducer;
